import ChartStyles from '@/utils/chart_styles';

/**
 * Crosshair Plugin for Chart.js
 * 
 * Provides synchronized crosshair functionality across multiple charts
 * without external dependencies.
 * 
 * Features:
 * - Vertical crosshair line that follows mouse
 * - Syncs across all charts on the page
 * - Shows intersection dots on all datasets
 * - Triggers Chart.js hover events on synced charts
 * 
 * Usage:
 *   import CrosshairPlugin from '@/utils/crosshair_plugin';
 *   Chart.register(CrosshairPlugin);
 * 
 * Configuration:
 *   plugins: {
 *     crosshair: {
 *       snap: {
 *         enabled: true
 *       },
 *       line: {
 *         color: '#somecolor',
 *         width: 1,
 *         dashPattern: [4]
 *         drawUnderChart: false
 *       },
 *       zoom: {
 *         enabled: false
 *       }
 *     }
 *   }
 */

const CrosshairPlugin = {
  id: 'crosshair',
  
  afterInit(chart) {
    // Initialize crosshair state
    chart._crosshairX = null;
    chart._crosshairIndex = null;
    
    // Set up sync event listener
    chart._syncHandler = (event) => {
      // Don't sync to self
      if (event.detail.chartId === chart.id) return;
      
      const index = event.detail.index;
      const xScale = chart.scales.x;
      
      if (!xScale) return;
      
      if (index === null || index === undefined) {
        // Create a mouseout event to clear crosshair
        const clearEvent = {
          type: 'mouseout',
          chart: chart,
          x: 0,
          y: 0,
          native: { type: 'mouseout' }
        };
        chart._eventHandler(clearEvent);
        return;
      }
      
      // Get the data point for this index
      const meta = chart.getDatasetMeta(0);
      if (!meta || !meta.data[index]) return;
      
      const dataPoint = meta.data[index];
      const yScale = chart.scales.y;
      
      // Create a fake mousemove event at this position
      const syntheticEvent = {
        type: 'mousemove',
        chart: chart,
        x: dataPoint.x,
        y: yScale ? (yScale.top + yScale.bottom) / 2 : 0,
        native: {
          type: 'mousemove',
          buttons: 0
        }
      };
      
      chart._crosshairX = dataPoint.x;
      chart._crosshairIndex = index;
      
      // Feed it through Chart.js's event system - this triggers onHover!
      chart._eventHandler(syntheticEvent);
    };
    
    window.addEventListener('chart-crosshair-sync', chart._syncHandler);
  },
  
  afterDestroy(chart) {
    if (chart._syncHandler) {
      window.removeEventListener('chart-crosshair-sync', chart._syncHandler);
    }
  },
  
  afterEvent(chart, args) {
    const event = args.event;
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;
    const options = chart.options.plugins?.crosshair;
    
    // Exit if plugin is disabled or required scales are missing
    if (!options || options === false || !xScale || !yScale) return;
    
    // Track if mouse is over chart
    const isOverChart = event.type !== 'mouseout' && 
                       event.x >= xScale.left && 
                       event.x <= xScale.right &&
                       event.y >= yScale.top &&
                       event.y <= yScale.bottom;
    
    if (!isOverChart) {
      if (chart._crosshairX !== null) {
        chart._crosshairX = null;
        chart._crosshairIndex = null;
        
        // Notify other charts to clear crosshair
        window.dispatchEvent(new CustomEvent('chart-crosshair-sync', {
          detail: { chartId: chart.id, index: null }
        }));
        
        chart.draw();
      }
      return;
    }
    
    // Check if snap is enabled (defaults to false to match original plugin)
    const snapEnabled = options.snap?.enabled ?? false;
    
    // Get x position and index
    let x, index;
    const activeElements = chart.getActiveElements();
    
    if (snapEnabled && activeElements.length > 0) {
      // Snap to active element if snap is enabled
      x = activeElements[0].element.x;
      index = activeElements[0].index;
    } else {
      // Otherwise use mouse position and find closest index
      x = event.x;
      const meta = chart.getDatasetMeta(0);
      if (meta && meta.data.length > 0) {
        let closestIndex = 0;
        let closestDistance = Math.abs(meta.data[0].x - x);
        
        for (let i = 1; i < meta.data.length; i++) {
          const distance = Math.abs(meta.data[i].x - x);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = i;
          }
        }
        index = closestIndex;
        if (snapEnabled) {
          x = meta.data[index].x;
        }
      }
    }
    
    if (chart._crosshairX !== x || chart._crosshairIndex !== index) {
      chart._crosshairX = x;
      chart._crosshairIndex = index;
      
      // Notify other charts to sync
      window.dispatchEvent(new CustomEvent('chart-crosshair-sync', {
        detail: { chartId: chart.id, index: index }
      }));
      
      chart.draw();
    }
  },
  
  // Draw crosshair BEFORE datasets if drawUnderChart is true
  beforeDatasetsDraw(chart) {
    const options = chart.options.plugins?.crosshair;
    if (!options || options === false) return;
    
    const drawUnderChart = options.line?.drawUnderChart ?? false;
    
    if (drawUnderChart) {
      this._drawCrosshair(chart);
    }
  },
  
  // Draw crosshair AFTER datasets if drawUnderChart is false (default)
  afterDraw(chart) {
    const options = chart.options.plugins?.crosshair;
    if (!options || options === false) return;
    
    const drawUnderChart = options.line?.drawUnderChart ?? false;
    
    if (!drawUnderChart) {
      this._drawCrosshair(chart);
    }
  },
  
  // Helper method to draw the crosshair and dots
  _drawCrosshair(chart) {
    if (chart._crosshairX === null || chart._crosshairX === undefined) return;
    if (chart._crosshairIndex === null || chart._crosshairIndex === undefined) return;
    
    const yScale = chart.scales.y;
    if (!yScale) return;
    
    const ctx = chart.ctx;
    const options = chart.options.plugins?.crosshair || {};
    
    ctx.save();
    
    // Draw the vertical line
    ctx.beginPath();
    ctx.setLineDash(options.line?.dashPattern || [4]);
    ctx.moveTo(chart._crosshairX, yScale.top);
    ctx.lineTo(chart._crosshairX, yScale.bottom);
    ctx.lineWidth = options.line?.width || 1;
    ctx.strokeStyle = options.line?.color || ChartStyles.colors.blueGrayLight;
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Draw intersection dots for each dataset
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      
      // Skip hidden datasets
      if (meta.hidden) return;
      
      // Get the data point at the crosshair index
      const dataPoint = meta.data[chart._crosshairIndex];
      if (!dataPoint) return;
      
      // Draw the dot
      ctx.beginPath();
      ctx.arc(chart._crosshairX, dataPoint.y, 4, 0, 2 * Math.PI, false);
      ctx.fillStyle = dataset.borderColor || dataset.pointBackgroundColor;
      ctx.fill();
      // Draw a border around the dot
      // cts.fillStyle = 'white';
      // ctx.lineWidth = 2;
      // ctx.strokeStyle = dataset.borderColor || dataset.pointBackgroundColor;
      // ctx.stroke();
    });
    
    ctx.restore();
  }
}

export default CrosshairPlugin;